import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { NbThemeService } from '@nebular/theme';
import { Observable, Subject, interval, of, throwError } from 'rxjs';
import { map, catchError, mergeMap, retry, takeUntil } from 'rxjs/operators';
import { BaseService } from '../base.service';
import { WINDOW } from '@core/http/window.providers';
import { LayoutProperties, SetupResponse } from './setup.interface';
import { getSetupTheme } from './setup.consts';
import { LanguageService } from '../language.service';
import { LANGUAGES, positions } from '@core/constants';
import { LocalStorage } from '@core/localstorage';

@Injectable({
    providedIn: 'root',
})
export class SetupService extends BaseService {
    /**
     * layout
     */
    private _layout: LayoutProperties;
    private _contact: any;

    public get contact() {
        return this._contact;
    }

    public set contact(value) {
        this._contact = value;
    }

    get layout() {
        return this._layout;
    }

    set layout(layout) {
        this._layout = layout;
    }

    get components() {
        return this.layout?.components;
    }

    get colors() {
        return this.layout?.colors;
    }

    get theme() {
        return this.layout?.theme;
    }

    get title() {
        return this.layout?.title;
    }

    private _images: LayoutProperties['images'];

    get images() {
        return this._images;
    }

    set images(images) {
        this._images = images;
    }

    public urlReturn: string | null;
    public proceedToFiliation: number;
    public proceedToFiliationDescription: string;
    public urlAppTi: string;
    public textAbout: string;

    constructor(
        private http: HttpClient,
        @Inject(DOCUMENT) private document: Document,
        @Inject(WINDOW) private window: Window,
        private themeService: NbThemeService,
        private languageService: LanguageService
    ) {
        super();
    }

    initialize(): Observable<any> {
        const body = this.setBody();
        return this.http.post<SetupResponse>(this.urlService + this.routeAdress.route_setup, body.toString()).pipe(
            map((response) => {
                if (!response.registers) return response;
                const {
                    layout,
                    contactData,
                    contactFones,
                    contactHours,
                    googleAnalytics,
                    urlReturn,
                    affiliationRedirect,
                    affiliationObsRedirect,
                    url_api_ti,
                    language,
                    language_initial,
                    textAbout,
                    sport,
                } = response.registers;
                this.layout = layout;
                this.contact = { ...contactData, phones: contactFones, officeHours: contactHours };
                this.setSport(sport);
                this.setTheme(layout.theme);
                this.setColors();
                this.setFavicon();
                this.setAnalytics(googleAnalytics);
                this.configAcessibility(
                    layout.acessibilidade,
                    language_initial || response.tokenParameter.languageFile
                );
                this.setFooterImage();
                this.images = layout.images;
                this.urlReturn = urlReturn?.return_url;
                this.proceedToFiliation = affiliationRedirect;
                this.proceedToFiliationDescription = affiliationObsRedirect;
                this.urlAppTi = url_api_ti;
                this.textAbout = textAbout;
                this.setupLanguageFallback(language_initial, language);
                return response.registers;
            }),
            catchError(this.serviceError)
        );
    }

    protected setSport(sport) {
        if (!sport) return;
        this.localStorage.saveSport(sport?.description?.toUpperCase() || 'TENNIS');
    }

    protected setTheme(theme) {
        const selectedTheme = getSetupTheme(theme);
        if (selectedTheme !== 'default') {
            this.themeService.changeTheme(selectedTheme);
        }
    }

    protected setColors() {
        if (!this.colors) return;
        this.document.documentElement.style.setProperty('--brand', this.colors.primary);
        this.document.documentElement.style.setProperty('--brand-light', this.colors.secondary);
    }

    getPartners(position: number, formatType: number, partnerType?: number, depto?: number) {
        const body = this.setBody();
        if (partnerType != null) {
            body.set('partnerType', partnerType);
        }

        if (depto != null) {
            body.set('depto', depto);
        }

        body.set('formatType', formatType);
        body.set('position', position);
        return this.http
            .post(this.urlService + this.routeAdress.route_partners, body.toString())
            .pipe(map((r) => r || {}));
    }

    setFavicon() {
        const faviconElement = this.document.getElementById('favicon');
        if (!faviconElement) return;
        if (faviconElement.getAttribute('href')) return;

        faviconElement.setAttribute('href', this.layout.favicon);
    }

    configAcessibility(enable, language) {
        setTimeout(() => {
            if (!enable) {
                (this.window as any).UserWay?.iconVisibilityOff();
            }
            if (language) {
                this.languageService.changeWidgetLanguage(language);
            }
        }, 1500);
    }

    setupLanguageFallback(language, languageList: string[]) {
        const localLanguages: { selected: string; available: string[] } = this.localStorage.getUserLangs();
        if (localLanguages) {
            const allLanguages = [localLanguages.selected, ...localLanguages.available];

            if (
                languageList.filter((item: string) => allLanguages.findIndex((lng: string) => item != lng) === -1)
                    .length > 0
            ) {
                this.localStorage.resetLangs();
            }
        }

        this.languageService.setupLanguages(language, languageList);
    }

    protected setAnalytics(trackingId) {
        if (!trackingId) return;
        const gaScript = this.document.createElement('script');
        gaScript.setAttribute('async', 'true');
        gaScript.setAttribute('src', `https://www.googletagmanager.com/gtag/js?id=${trackingId}`);

        const gaScript2 = document.createElement('script');
        gaScript2.innerText = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date());gtag(\'config\', \'${trackingId}\');`;

        this.document.documentElement.firstChild.appendChild(gaScript);
        this.document.documentElement.firstChild.appendChild(gaScript2);
    }

    public setFooterImage() {
        const footerImage = this.layout?.images?.footer;
        const footerEl = document.querySelector<HTMLElement>('.footer');
        if (footerImage && footerEl) {
            footerEl.style.backgroundImage = `url(${footerImage}`;
        }
    }

    public setFooterImageFromDepartment(department: string) {
        const footerImage = this.layout?.images?.footers?.[department];
        const done$ = new Subject();
        const source = interval(1000);
        const pipe = source.pipe(
            mergeMap(() => {
                const footerEl = document.querySelector<HTMLElement>('.footer');
                if (!footerEl) return throwError('footer not loaded yet');

                return of(footerEl);
            }),
            retry(2),
            takeUntil(done$)
        );

        const sub = pipe.subscribe(
            (footerEl) => {
                if (footerImage) {
                    footerEl.style.backgroundImage = `url(${footerImage}`;
                    done$.next();
                }
            },
            (err) => {
                console.log(err);
            },
            () => {
                sub.unsubscribe();
            }
        );
    }
}
